import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { useA11ySelector } from '@sg/a11y-helpers';
import { useNoticeBannersStackActions, useResponsive } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import { AccountScreenContainer, AccountScreenSection, AccountScreenTitle } from '../../components';
import {
  useCustomerProfileFieldsQuery,
  useUpdateCustomerProfileMutation,
} from './GraphQL/Profile.generated';
import { ProfileForm } from './ProfileForm';

// ────────────────────────────────────────────────────────────────────────────────

export const ProfileScreen = () => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const a11ySelector = useA11ySelector('account.profile.container');

  // ─── Network Requests ────────────────────────────────────────────────

  const [customerResponse] = useCustomerProfileFieldsQuery({
    requestPolicy: 'cache-and-network',
  });
  const { submitResponse, handleFormSubmit } = useFormSubmit();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoading = customerResponse.fetching || !customerResponse.data?.currentCustomer?.id;

  // NOTE: To improve UX, we need to close this screen at the smallest breakpoint (mobile device) on form reset.
  const shouldCloseTheScreenOnFormReset = currentBreakpoint.isXS;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onFormReset = useCallback(() => {
    if (!shouldCloseTheScreenOnFormReset) return;

    router.back();
  }, [shouldCloseTheScreenOnFormReset, router]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainer screen="profile" testID="profile-screen" {...a11ySelector.props}>
      <AccountScreenTitle title={formatMessage(messages.accountProfileTitle)} />

      <AccountScreenSection style={styles.content}>
        <ProfileForm
          customer={customerResponse.data?.currentCustomer ?? null}
          isLoading={isLoading}
          isSubmitting={submitResponse.fetching}
          submitForm={handleFormSubmit}
          onFormReset={onFormReset}
        />
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

type UpdateCallback = ReturnType<typeof useUpdateCustomerProfileMutation>[1];

// ────────────────────────────────────────────────────────────────────────────────

const useFormSubmit = () => {
  const { formatMessage } = useIntl();
  const [submitResponse, updateMutation] = useUpdateCustomerProfileMutation();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const { track } = useTelemetry();

  const handleFormSubmit = React.useCallback(
    async (input: Parameters<UpdateCallback>[0]) => {
      const response = await updateMutation(input);
      const systemError = getUrqlError(response.error);

      if (systemError) {
        const userError = formatMessage(messages.accountProfileError);

        track('account.profile_save_failed', { userError, systemError });

        addNoticeBanner({
          text: userError,
          palette: 'caution',
        });
      }

      return response;
    },
    [updateMutation, formatMessage, track, addNoticeBanner],
  );

  return { submitResponse, handleFormSubmit };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountProfileTitle: {
    defaultMessage: 'Account info',
    description: 'Account | Profile | Title',
  },
  accountProfileError: {
    defaultMessage: 'Oops! We were unable to save your changes. Try again in a bit.',
    description: 'Account | Profile | Error',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flexGrow: 0,
  },
});
